// Тумблер «Траектории по HSI» («живая вода») на карте течений. Включает
// отдельный слой обратного лагранжева сноса ненулевых HSI-ячеек вида (порт
// правой панели eln). Длину усика двигает ползунок дней внизу карты — та же
// лотовая линия, что и снос косяков; origin (7 дн назад) показан кружком.
//
// Снос косяков (setDrift) — независимый слой, этот тумблер его не трогает.
import { HintDot } from '../help/HintDot'
import { useT } from '../i18n'
import { driftGradient, driftScaleEdges } from '../map/hsiDrift'
import { useLayerPalette } from '../maps/useLayerPalette'
import { hsiDriftNote, hsiHistoryNote, type HsiHistoryMeta } from './hsiHistoryNote'

interface Props {
  enabled: boolean
  /** Вид не выбран (или «Все виды») — HSI-поля нет, включать нечего. */
  disabled: boolean
  onToggle: (on: boolean) => void
  /** Сколько усиков в кадре — короткая обратная связь под тумблером. */
  count?: number | null
  /** meta ответа слоя: по нему объясняем и дыры в истории течений, и саму
   *  пустоту (устаревшее поле вида, нулевое поле, отсутствие течений). */
  meta?: HsiHistoryMeta | null
  /** Пересчёт поля вида идёт прямо сейчас — подпись говорит «ждите», а не
   *  отсылает к кнопке, которая уже нажата. */
  recomputing?: boolean
  /** Докачка истории течений идёт прямо сейчас — тогда подпись про дыру говорит
   *  «работа идёт», а не зовёт нажать «Обновить» ещё раз (заказать её повторно
   *  всё равно нельзя: очередь не примет вторую задачу на те же сутки). */
  downloading?: boolean
  /**
   * Идут ли кометы НА САМОМ ДЕЛЕ (производный флаг страницы, не «нажата кнопка»):
   * кнопка обязана описывать состояние карты, а не намерение пользователя.
   */
  animating: boolean
  /** Системное «меньше движения» — анимация запрещена, и об этом надо сказать. */
  reduced?: boolean
  onToggleAnimation: (on: boolean) => void
}

export function HsiDriftControl({
  enabled,
  disabled,
  onToggle,
  count,
  meta,
  recomputing = false,
  downloading = false,
  animating,
  reduced = false,
  onToggleAnimation,
}: Props) {
  // Выключенной кнопка бывает по двум разным причинам, и обе не должны запирать
  // пользователя в «нажатом» состоянии:
  //  • нечего анимировать (усиков нет) — но если анимация УЖЕ идёт, кнопку не
  //    блокируем никогда, иначе уход в пустой район запирает режим без выхода;
  //  • системное «меньше движения» — тут запрет честный и с пояснением ниже,
  //    молча игнорировать нажатие хуже, чем не дать нажать.
  const animDisabled = (!count && !animating) || reduced
  const t = useT()
  // Шкала усиков — ровно та, что красит растр HSI под ними (слой `probability`
  // каталога). Своя гамма показывала бы 60 цветом семидесяти.
  const palette = useLayerPalette('probability')
  const edges = driftScaleEdges(palette)
  const emptyNote = hsiDriftNote(meta, { recomputing })
  const historyNote = hsiHistoryNote(meta, { downloading })
  return (
    <div>
      <div className="eyebrow mb-3 flex items-center gap-1.5">
        {t('models.hsiDrift.title')}
        <HintDot id="hsiDrift" />
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          aria-label={t('models.hsiDrift.toggleAria')}
          disabled={disabled}
          onClick={() => !disabled && onToggle(!enabled)}
          className={
            'relative ml-auto h-4 w-7 rounded-full transition-colors ' +
            (disabled ? 'cursor-not-allowed bg-hull opacity-50' : enabled ? 'bg-sonar' : 'bg-hull')
          }
        >
          <span
            className="absolute top-0.5 h-3 w-3 rounded-full bg-white transition-all"
            style={{ left: enabled ? 14 : 2 }}
          />
        </button>
      </div>

      {disabled && (
        <div className="text-[11px] leading-snug text-buoy">
          {t('models.hsiDrift.pickSpecies')}
        </div>
      )}

      {enabled && !disabled && (
        <div className="rounded-lg border border-hull bg-trench px-3 py-2.5 text-mist">
          <div className="mb-1 h-2 w-full rounded" style={{ background: driftGradient(palette) }} />
          {/* Подписи краёв — значения индекса, как у легенды слоя HSI. Прежнее
              «ниже индекса 86» описывало нормировку на p98 поля, которой больше
              нет: усик обязан совпадать с картой под ним. */}
          <div className="flex justify-between tnum text-[10px]">
            <span>{edges ? edges[0] : t('maps.legend.min')}</span>
            <span>{edges ? edges[1] : t('maps.legend.max')}</span>
          </div>
          {/* Объяснение «откуда пришла вода» переехало в подсказку «i» у
              заголовка (правка владельца 2026-08-08): в панели оно занимало
              три строки над кнопкой, которую ищут глазами. */}
          {count != null && count > 0 && (
            <div className="mt-1.5 tnum text-[11px]">{t('models.hsiDrift.pointsInView', { count })}</div>
          )}
          {/* Почему усиков нет — своими словами. Пустая карта при включённом
              тумблере молчать не имеет права (регрессия 11.08.2026). */}
          {emptyNote && (
            <div className="mt-1.5 text-[10px] leading-snug text-buoy">
              {t(emptyNote.key, emptyNote.vars)}
            </div>
          )}
          {historyNote && (
            <div className="mt-1.5 text-[10px] leading-snug text-buoy">
              {t(historyNote.key, historyNote.vars)}
            </div>
          )}
          <button
            type="button"
            aria-pressed={animating}
            onClick={() => !animDisabled && onToggleAnimation(!animating)}
            disabled={animDisabled}
            className={
              'mt-2 w-full rounded-lg border px-3 py-1.5 text-[12px] transition-colors ' +
              (animating
                ? 'border-sonar bg-sonar text-white'
                : 'border-hull bg-trench text-mist hover:border-sonar') +
              (animDisabled ? ' cursor-not-allowed opacity-50' : '')
            }
          >
            {animating ? `⏸ ${t('models.hsiDrift.stop')}` : `▶ ${t('models.hsiDrift.animate')}`}
          </button>
          {reduced && (
            <div className="mt-1 text-[11px] leading-snug text-faint">
              {t('models.hsiDrift.reducedMotion')}
            </div>
          )}
          {animating && (
            <div className="mt-1 text-[11px] leading-snug text-faint">
              {t('models.hsiDrift.cometsOnly')}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
